import { useState, useEffect, useCallback, useRef } from 'react';
import { io, type Socket } from 'socket.io-client';
import { getIdToken } from '@/lib/firebase';
import { SOCKET_URL } from '@/lib/config';

const isDev = import.meta.env.DEV;
const debug = (...args: unknown[]) => { if (isDev) console.log(...args); };

type ConnectionStatus = 'connecting' | 'connected' | 'disconnected' | 'error';

interface ConnectionInfo {
  status: ConnectionStatus;
  reconnectAttempts: number;
  lastConnected?: Date;
  latency?: number;
  error?: string;
}

interface UseConnectionStatusReturn {
  connectionInfo: ConnectionInfo;
  isOnline: boolean;
  retry: () => void;
  socket: Socket | null;
}

// Global connection state to prevent multiple concurrent connections
let globalSocket: Socket | null = null;
let globalUserId: string | null = null;
let isConnecting = false;
let lastConnectionAttempt = 0;

export function useConnectionStatus(userId: string | null): UseConnectionStatusReturn {
  const [connectionInfo, setConnectionInfo] = useState<ConnectionInfo>({
    status: 'disconnected',
    reconnectAttempts: 0,
  });
  const [socket, setSocket] = useState<Socket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout>();
  const pingIntervalRef = useRef<NodeJS.Timeout>();
  const reconnectAttemptsRef = useRef(0); // Ref to avoid stale closure in event handlers
  const maxReconnectAttempts = 3; // Reduced from 5
  const reconnectDelay = 5000; // Increased from 2000

  // Monitor browser online/offline status
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const connectSocket = useCallback(async () => {
    if (!userId || !isOnline) return;

    const now = Date.now();
    const timeSinceLastAttempt = now - lastConnectionAttempt;

    if (timeSinceLastAttempt < 2000 || isConnecting) return;

    // If we have a global socket for the same user, reuse it
    if (globalSocket && globalUserId === userId && globalSocket.connected) {
      debug('Reusing existing global socket connection');
      setSocket(globalSocket);
      setConnectionInfo(prev => ({
        ...prev,
        status: 'connected',
        reconnectAttempts: 0,
        error: undefined
      }));
      return;
    } else if (globalSocket && globalUserId === userId && !globalSocket.connected) {
      globalSocket.disconnect();
      globalSocket = null;
      globalUserId = null;
    }

    // Clean up any existing global socket if user changed
    if (globalSocket && globalUserId !== userId) {
      globalSocket.disconnect();
      globalSocket = null;
      globalUserId = null;
    }

    isConnecting = true;
    lastConnectionAttempt = now;
    debug('Creating new socket connection for user:', userId);

    setConnectionInfo(prev => ({
      ...prev,
      status: 'connecting',
      error: undefined
    }));

    // Fetch a fresh Firebase ID token so the server can verify identity at the
    // handshake. `auth` is the verified source of truth; `query.userId` remains
    // for the server's dev-only fallback when token verification isn't available.
    const token = await getIdToken();

    const newSocket = io(SOCKET_URL, {
      path: "/socket.io/",
      transports: ['polling', 'websocket'], // Allow both polling and websocket
      autoConnect: true,
      timeout: 20000,
      forceNew: false,
      upgrade: true,
      reconnection: true, // Enable auto-reconnection
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      auth: token ? { token } : {},
      query: {
        userId: userId
      }
    });

    // Ping to measure latency
    const startPing = () => {
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }

      pingIntervalRef.current = setInterval(() => {
        if (newSocket.connected) {
          const startTime = Date.now();
          newSocket.emit('ping', startTime, (response: number) => {
            const latency = Date.now() - response;
            setConnectionInfo(prev => ({ ...prev, latency }));
          });
        }
      }, 30000);
    };

    newSocket.on('connect', () => {
      debug('Socket connected. ID:', newSocket.id);
      isConnecting = false;

      // Identify this user to the server
      newSocket.emit('identify-user', userId);

      // Dispatch event to request profile sync (restores server memory after restart)
      window.dispatchEvent(new CustomEvent('vyzle-chat-socket-connected', {
        detail: { userId }
      }));

      // Set as global socket
      globalSocket = newSocket;
      globalUserId = userId;

      reconnectAttemptsRef.current = 0;
      setConnectionInfo(prev => ({
        ...prev,
        status: 'connected',
        lastConnected: new Date(),
        reconnectAttempts: 0,
        error: undefined
      }));
      setSocket(newSocket);
      startPing();
    });

    // Handle forced disconnection from server
    newSocket.on('force-disconnect', (data: { reason: string }) => {
      debug('Force disconnected by server:', data.reason);
      isConnecting = false;

      if (data.reason === 'new-connection' || data.reason === 'too-many-connections') {
        // Don't try to reconnect
        setConnectionInfo(prev => ({
          ...prev,
          status: 'disconnected',
          error: data.reason === 'new-connection'
            ? 'Connected from another device/tab'
            : 'Too many connections'
        }));
        setSocket(null);

        // Clear global socket if it's this one
        if (globalSocket === newSocket) {
          globalSocket = null;
          globalUserId = null;
        }

        newSocket.disconnect();
        return;
      }

      if (data.reason === 'too-rapid-reconnect') {
        // Wait longer before next attempt
        setTimeout(() => {
          if (reconnectAttemptsRef.current < maxReconnectAttempts) {
            reconnectAttemptsRef.current += 1;
            setConnectionInfo(prev => ({
              ...prev,
              reconnectAttempts: reconnectAttemptsRef.current
            }));
            connectSocket();
          }
        }, reconnectDelay * 2);
      }
    });

    newSocket.on('connect_error', (error) => {
      console.error('Socket connection error:', error.message);
      isConnecting = false;

      setConnectionInfo(prev => ({
        ...prev,
        status: 'error',
        error: error.message || 'Connection failed'
      }));

      // Clear global socket on error
      if (globalSocket === newSocket) {
        globalSocket = null;
        globalUserId = null;
      }

      // Attempt reconnection if under limit
      if (reconnectAttemptsRef.current < maxReconnectAttempts) {
        reconnectTimeoutRef.current = setTimeout(() => {
          reconnectAttemptsRef.current += 1;
          setConnectionInfo(prev => ({
            ...prev,
            reconnectAttempts: reconnectAttemptsRef.current
          }));
          connectSocket();
        }, reconnectDelay * (reconnectAttemptsRef.current + 1));
      }
    });

    newSocket.on('disconnect', (reason) => {
      debug('Socket disconnected:', reason);
      isConnecting = false;

      setConnectionInfo(prev => ({
        ...prev,
        status: 'disconnected'
      }));
      setSocket(null);

      // Clear global socket if it's this one
      if (globalSocket === newSocket) {
        globalSocket = null;
        globalUserId = null;
      }

      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }

      // Only auto-reconnect in production and for specific disconnect reasons
      if (reason === 'io server disconnect' && import.meta.env.PROD) {
        setTimeout(() => {
          if (reconnectAttemptsRef.current < maxReconnectAttempts) {
            reconnectAttemptsRef.current += 1;
            setConnectionInfo(prev => ({
              ...prev,
              reconnectAttempts: reconnectAttemptsRef.current
            }));
            connectSocket();
          }
        }, reconnectDelay);
      }
    });

    return newSocket;
  }, [userId, isOnline, connectionInfo.reconnectAttempts]);

  const retry = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }

    reconnectAttemptsRef.current = 0;
    setConnectionInfo(prev => ({
      ...prev,
      reconnectAttempts: 0,
      error: undefined
    }));

    connectSocket();
  }, [connectSocket]);

  // Initial connection - only connect if we don't have a global socket for this user
  useEffect(() => {
    if (userId && isOnline) {
      if (globalSocket && globalUserId === userId && globalSocket.connected) {
        setSocket(globalSocket);
        setConnectionInfo(prev => ({
          ...prev,
          status: 'connected',
          reconnectAttempts: 0,
          error: undefined
        }));
      } else if (!socket) {
        connectSocket();
      }
    }

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (pingIntervalRef.current) {
        clearInterval(pingIntervalRef.current);
      }
    };
  }, [userId, isOnline, socket]);

  // Handle browser online/offline events
  useEffect(() => {
    if (!isOnline && socket) {
      socket.disconnect();
      setConnectionInfo(prev => ({ ...prev, status: 'disconnected' }));
      setSocket(null);
      if (globalSocket === socket) {
        globalSocket = null;
        globalUserId = null;
      }
    } else if (isOnline && !socket && userId) {
      connectSocket();
    }
  }, [isOnline, socket, userId, connectSocket]);

  // Reconnect on tab/app foreground. On mobile, the OS often kills the
  // socket transport silently when the screen locks; the client doesn't
  // realize the connection is dead until the next emit times out (~20s),
  // which surfaces as the QA-reported "timeout error after unlock". Force
  // a fresh connection as soon as the document becomes visible again.
  useEffect(() => {
    if (!userId) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;

      // The OS might have killed the underlying transport. socket.connected
      // can lie here (it reflects the last known state, not a fresh probe),
      // so reset our reconnect counter and let connectSocket figure out
      // whether to reuse or replace the global socket.
      reconnectAttemptsRef.current = 0;

      if (!globalSocket || !globalSocket.connected) {
        connectSocket();
        return;
      }

      // Probe the existing socket — if a ping doesn't round-trip in a
      // couple of seconds, the transport is dead. Force a reconnect.
      const probeTimeout = setTimeout(() => {
        if (globalSocket) {
          globalSocket.disconnect();
          globalSocket = null;
          globalUserId = null;
        }
        connectSocket();
      }, 2500);

      try {
        globalSocket.emit('ping', Date.now(), () => {
          clearTimeout(probeTimeout);
        });
      } catch {
        clearTimeout(probeTimeout);
        connectSocket();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    // pageshow handles iOS bfcache restores, where visibilitychange may not fire.
    window.addEventListener('pageshow', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pageshow', handleVisibilityChange);
    };
  }, [userId, connectSocket]);

  // Clean up on unmount or user change
  useEffect(() => {
    return () => {
      if (socket && globalSocket === socket) {
        // Don't disconnect immediately to allow for HMR reuse
        setTimeout(() => {
          if (globalSocket && !document.contains(document.documentElement)) {
            globalSocket.disconnect();
            globalSocket = null;
            globalUserId = null;
          }
        }, 1000);
      }
    };
  }, [userId]);

  // Ensure we always return the global socket if it exists and is connected
  const finalSocket = socket || (globalSocket && globalUserId === userId && globalSocket.connected ? globalSocket : null);

  return {
    connectionInfo,
    isOnline,
    retry,
    socket: finalSocket
  };
} 